/**
 * WebSearchToggle Component
 *
 * This component allows users to enable/disable web search for AI responses.
 * When enabled, the AI can search the web for current information to enhance responses.
 *
 * BACKEND INTEGRATION GUIDE:
 *
 * When the backend API is ready to support web search:
 *
 * 1. Include the web search enabled flag in the message payload:
 *    ```typescript
 *    const payload = {
 *      message: input,
 *      webSearchEnabled: enabled, // Send this to backend
 *      sessionId,
 *      skill,
 *    };
 *    ```
 *
 * 2. Backend should use this flag to:
 *    - Enable web search tools during agent execution
 *    - Include web search results in context
 *    - Cite web sources in responses
 *
 * 3. Update the agent tools to include web search capabilities
 *
 * 4. Consider adding rate limiting or authentication for web search
 *
 * Current implementation: Stores state locally, ready for backend integration
 */

import { Globe } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import type { WebSearchToggleProps } from "./types";

export function WebSearchToggle({
  enabled,
  onChange,
  disabled = false,
}: WebSearchToggleProps) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2">
            <Globe
              className={`h-4 w-4 ${enabled ? 'text-primary' : 'text-muted-foreground'}`}
            />
            <Switch
              checked={enabled}
              onCheckedChange={onChange}
              disabled={disabled}
              aria-label="Toggle web search"
            />
            <span className="text-xs md:text-sm text-muted-foreground hidden sm:inline">
              Web Search
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p>
            {enabled
              ? 'Web search enabled - AI can search the web'
              : 'Web search disabled - AI uses knowledge base only'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
